import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react';
import Slider, { Handle } from 'rc-slider';
import 'rc-slider/assets/index.css';
import FaceStore from '../stores/FaceStore';
import { MAX_MATCH_THRESHOLD, MIN_MATCH_THRESHOLD, DEFAULT_MATCH_THRESHOLD } from '../constants';
import { BREAKPOINT_SIZE, COLORS, Strong, MARGIN, SectionHeading } from '../theme';

const Arrow = styled.div<{ isRight?: boolean }>`
  content: '';
  position: absolute;
  top: 9px;
  width: 7.4px;
  height: 7.4px;
  border-top: 2px solid ${COLORS.white};

  ${props =>
    props.isRight
      ? css`
          right: 20%;
          border-right: 2px solid ${COLORS.white};
          -moz-transform: rotate(45deg);
          -webkit-transform: rotate(45deg);
          transform: rotate(45);
        `
      : css`
          left: 20%;
          border-left: 2px solid ${COLORS.white};
          -moz-transform: rotate(-45deg);
          -webkit-transform: rotate(-45deg);
          transform: rotate(-45deg);
        `}
`;

const Line = styled.div`
  position: absolute;
  left: 20.5px;
  height: 8px;
  opacity: 0.7;
  border-left: 1px solid ${COLORS.lightGrey};
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: ${MARGIN.small};

  .rc-slider {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 20px;
    margin: 0 2rem;
    width: 100%;
    position: relative;

    @media (max-width: ${BREAKPOINT_SIZE.md}px) {
      margin: 0 0.5rem;
    }
  }

  .rc-slider-rail,
  .rc-slider-track {
    height: 8px;
    background: ${COLORS.blue};
  }

  .rc-slider-handle {
    position: absolute;
    display: flex;
    align-items: center;
    top: -2px;
    height: 25px;
    width: 42px;
    margin-top: 0;
    background: ${COLORS.teal};
    border: none;
    border-radius: 50px;


    :hover,
    :active,
    :focus {
      border: none;
      box-shadow: none;
    }
  }
  }

  .rc-slider-handle-click-focused {
    :focus {
      border: none;
      box-shadow: none;
    }
  }

  .rc-slider-step {
    height: 8px;
  }

  .rc-slider-dot {
    :first-child,
    :last-child {
      display: none;
    }
    position: absolute;
    margin: 0;
    width: 1.5px;
    height: 8px;
    bottom: 0;
    border: none;
    border-radius: 0;
    background-color: ${COLORS.white};
    cursor: pointer;
    vertical-align: middle;
  }
`;

const MinMaxLabel = styled.div`
  color: ${COLORS.blue};
  font-weight: bold;
  font-size: 16px;
`;

const StyledSlider = styled(Slider)`
  margin: 0 10px;
`;

const InfoBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  padding: 15px;
  margin-bottom: ${MARGIN.small};
  font-family: avenir-Medium, sans-serif;
  font-size: 12px;
  color: ${COLORS.white};
  background: ${COLORS.pink};
  border-radius: 3px;
`;

const onSliderChange = (newValue: number) => (FaceStore.matchThreshold = newValue);

const FaceSlider = () => (
  <>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: MARGIN.small }}>
      <SectionHeading style={{ marginBottom: 0 }}>Match threshold</SectionHeading>
    </div>

    <SliderContainer>
      <MinMaxLabel>{MIN_MATCH_THRESHOLD / 10}</MinMaxLabel>
      <StyledSlider
        min={MIN_MATCH_THRESHOLD}
        max={MAX_MATCH_THRESHOLD}
        defaultValue={DEFAULT_MATCH_THRESHOLD}
        onChange={onSliderChange}
        dots
        handle={props => {
          return (
            <Handle {...props}>
              <Arrow />
              <Line />
              <Arrow isRight />
            </Handle>
          );
        }}
      />
      <MinMaxLabel>{MAX_MATCH_THRESHOLD / 10}</MinMaxLabel>
    </SliderContainer>
    <InfoBox>
      <div>
        As the match threshold is lowered there is a greater chance that the wrong people are
        identified as potential matches — <Strong>false positive</Strong>. <br />
        As the match threshold is increased there is a greater possibility that someone is not
        identified as a potential match — <Strong>false negative</Strong>.
      </div>
    </InfoBox>
  </>
);

export default observer(FaceSlider);
